import { Link } from 'react-router-dom';
import { Bed, Users, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCoverImageUrl } from '@/hooks/useMediaAssets';
import { useTranslate } from '@/i18n';

interface ProducerAccommodationsProps {
  producerId?: string;
  title?: string;
  maxItems?: number;
}

function formatPriceLabel(text: string | null | undefined): string | null {
  if (!text) return null;
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (/kr|per|natt/i.test(trimmed)) return trimmed;
  if (/^\d+$/.test(trimmed)) return `Från ${trimmed} kr per natt`;
  return trimmed;
}

interface AccommodationCardData {
  id: string;
  title: string;
  description_short: string | null;
  beds: number | null;
  guest_capacity: number | null;
  price_per_night_text: string | null;
}

function AccommodationCard({ acc }: { acc: AccommodationCardData }) {
  const t = useTranslate();
  const coverUrl = useCoverImageUrl('accommodation', acc.id, 640);
  const priceLabel = formatPriceLabel(acc.price_per_night_text);

  return (
    <Link to={`/accommodation/${acc.id}`} className="block group">
      <Card className="h-full transition-shadow group-hover:shadow-md overflow-hidden">
        {/* Cover image or fallback */}
        {coverUrl ? (
          <div className="aspect-[16/10] overflow-hidden">
            <img
              src={coverUrl}
              alt={acc.title}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
              loading="lazy"
            />
          </div>
        ) : (
          <div className="aspect-[16/10] bg-muted flex items-center justify-center">
            <Bed className="h-8 w-8 text-muted-foreground/40" />
          </div>
        )}

        <CardContent className="pt-4 pb-4 space-y-2.5">
          <h3 className="font-medium text-foreground group-hover:text-primary transition-colors line-clamp-1">
            {acc.title}
          </h3>

          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            {acc.beds && (
              <span className="flex items-center gap-1">
                <Bed className="h-3 w-3" /> {acc.beds} bäddar
              </span>
            )}
            {acc.guest_capacity && (
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" /> Max {acc.guest_capacity}
              </span>
            )}
          </div>

          {acc.description_short && (
            <p className="text-sm text-muted-foreground line-clamp-2">
              {acc.description_short}
            </p>
          )}

          {priceLabel ? (
            <p className="text-sm font-medium text-foreground">{priceLabel}</p>
          ) : (
            <p className="text-xs text-muted-foreground italic">Pris visas vid förfrågan</p>
          )}

          <Button variant="link" className="p-0 h-auto text-sm" tabIndex={-1}>
            {t("producer.accommodations.view")} <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </CardContent>
      </Card>
    </Link>
  );
}

/**
 * Public-facing accommodation section for the producer detail page.
 * Renders only when the producer has public accommodations.
 */
export function ProducerAccommodations({
  producerId,
  title,
  maxItems = 3,
}: ProducerAccommodationsProps) {
  const t = useTranslate();
  const heading = title ?? t("producer.accommodations.heading");
  const { data: accommodations, isLoading } = useQuery({
    queryKey: ['public-producer-accommodations', producerId],
    queryFn: async () => {
      if (!producerId) return [];
      const { data, error } = await supabase
        .from('producer_accommodations')
        .select('id, title, description_short, beds, guest_capacity, price_per_night_text')
        .eq('producer_id', producerId)
        .eq('status', 'public')
        .order('created_at', { ascending: false })
        .limit(maxItems);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!producerId,
    staleTime: 5 * 60_000,
  });

  if (!producerId || isLoading || !accommodations || accommodations.length === 0) return null;

  return (
    <section>
      <h2 className="font-display text-xl font-semibold text-foreground mb-4">{heading}</h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {accommodations.map((acc) => (
          <AccommodationCard key={acc.id} acc={acc} />
        ))}
      </div>
    </section>
  );
}
